/**
 * @file     constant-group.js - implement the ConstantGroup class, which does some runtime validation
 *                               of object properties in non-TS JS when running in a debug build. The
 *                               idea is that we can have groups of constants that will throw when we
 *                               try to access mis-spelled members in a debug build.
 *
 * @author   Wes Garland, wes@distributive.network
 * @date     Dec 2021, Jun 2025
 */
'use strict';
const isDebugBuild = require('dcp/common/dcp-build').build === 'debug';

exports.ConstantGroup = ConstantGroup;

/**
 * ConstantGroup ctor.
 * @example
 *    const g = new ConstantGroup({abc: 123, def: 456});
 *    console.log(g.abc); // logs 123
 *    const h = new ConstantGroup(['abc', 'def']);
 *    console.log(h.abc); // logs abc
 * @note ctor args are not the same as WorkerConstantGroup or SchedulerConstantGroup /wg jun 2025
 */
function ConstantGroup(defn)
{
  if (!Array.isArray(defn))
  {
    for (let el in defn)
      this[el] = defn[el];
  }
  else
  {
    for (let i=0; i < defn.length; i++)
    {
      const el = defn[i];
      this[el] = el;
    }
  }

  if (isDebugBuild)
  {
    let px = new Proxy(this, {
      get: (target, prop) => {
        if (target.hasOwnProperty(prop))
          return target[prop];
        if (target.constructor.prototype.hasOwnProperty(prop))
          return target.constructor.prototype[prop].bind(target);
        throw new Error(`no such constant: '${prop}'`);
      },
      set: (target, prop) => {
        throw new Error('constants are immutable!');
      },
      deleteProperty: (_prop) => {
        throw new Error('constant groups are immutable!');
      },
    });

    return px;
  }
}

/**
 * Lookup up the name of a constant based on its value. Value collisions will find the value which was
 * defined first in the ctor. Returns string or null when there is no such value.
 *
 * @example
 * switch(g.lookup(val)
 * {
 *    case 'abc':
 *      console.log('easy as', g[val]);
 *      break;
 *    default:
 *      console.error(`no constant with value ${val} in group`);
 *      break;
 * }
 */
ConstantGroup.prototype.lookup = function ConstantGroup$$lookup(value)
{
  for (let prop in this)
  {
    if (prop === value)
      return prop;
  }
}
